import { Observable } from "rxjs/Rx";
import { ActivatedRoute, Router } from "@angular/router";
import { Component, OnInit, ViewChild } from "@angular/core";
import { DataService } from "../../services/data.service";
import { Tag } from "../../models/tag.model";
import { ActivityLogComponent } from "./activity-log/activity-log.component";
import {
  MatTableDataSource,
  MatSort,
  MatTabGroup,
  MatTabChangeEvent,
} from "@angular/material";
import * as moment from "moment";
import { ToastService } from "../../components/toast/toast.service";
import { TagService } from "../../services/tag.service";
import Utils from "../../services/utils";
import { Patient } from "../../models/patient.model";
import { UploaderService } from "../../services/uploader.service";
import { GuidService } from "../../services/guid.service";
import { Bucket } from "../../models/bucket.enum";
import { SearchService } from "../../services/search.service";
import { PatientService } from "../../services/patient.service";
import { MetaDataRaw } from "../../models/meta-data.model";
import * as _ from "underscore";
import { LowyGridComponent } from "../../components/grid/grid.component";
import { Image } from "../../models/image.model";
import { AuthService, UserPermissions } from "../../services/auth.service";
import { DeleteConfirmationModalComponent } from "../../components/modal/modals/delete-confirmation-modal.component";
import { AdjudicationActivityLogComponent } from "./adjudication-activity-log/adjudication-activity-log.component";
import { AdjudicationService } from "../../services/adjudication.service";
import { ImageService } from "../../services/image.service";
import { ViewableImageWebPackge } from "../../models/viewable-image.model";
import { ViewableImageComponent } from "./viewable-image/viewable-image.component";
import { LoadingSpinnerService } from "../../components/loading-spinner/loading-spinner.service";

@Component({
  templateUrl: "./data-details.component.html",
  styleUrls: ["./data-details.component.scss"],
  providers: [
    DataService,
    TagService,
    UploaderService,
    GuidService,
    PatientService,
    AdjudicationService,
    ImageService,
    AuthService,
  ],
})
export class DataDetailsComponent implements OnInit {
  public userPermissions: UserPermissions;
  data: any;
  tabDetails: any;
  dataType: string;
  dataId: string;
  viewableImages: ViewableImageWebPackge;
  downloadLink: string;
  fileSize: string;
  tagGroups: {
    [key: string]: Tag;
  };

  imageQualityDataSource: MatTableDataSource<any>;
  imageQualityColumns: Array<string>;

  stagingDataSource: MatTableDataSource<any>;
  stagingColumns: Array<string>;

  detailsDataSource: MatTableDataSource<any>;
  detailsColumns: Array<string>;

  family: Array<Patient>;
  public pedigreePhotoURI: string;

  public fileInput;
  public fileInputForm;
  public pedigreePicture;
  public isRecentSearchResults: boolean; // used for back to results link

  public activeTab: string;
  public adjudicationActivityLogData: any;

  @ViewChild("activityLog") activityLog: ActivityLogComponent;
  @ViewChild("adjudicationActivityLog")
  adjudicationActivityLog: AdjudicationActivityLogComponent;
  @ViewChild("lowyDataTab") lowyDataTab: LowyGridComponent;
  @ViewChild("lowyDicomTab") lowyDicomTab: LowyGridComponent;
  @ViewChild("lowyFamilyTab") lowyFamilyTab: LowyGridComponent;
  @ViewChild("lowyMetaDataTab") lowyMetaDataTab: LowyGridComponent;
  @ViewChild("tabGroup") tabGroup: MatTabGroup;
  @ViewChild("deleteImageModal")
  deleteImageModal: DeleteConfirmationModalComponent;
  @ViewChild("deletePatientModal")
  deletePatientModal: DeleteConfirmationModalComponent;
  @ViewChild("viewableImageModal") viewableImageModal: ViewableImageComponent;

  // stored data for download
  private lowyDataTabData: Array<any>;
  private lowyDicomTabData: Array<any>;
  private lowyFamilyTabData: Array<any>;
  private lowyMetaDataTabData: Array<any>;

  constructor(
    private dataService: DataService,
    private route: ActivatedRoute,
    private router: Router,
    private toastService: ToastService,
    private tagService: TagService,
    private uploaderService: UploaderService,
    private guidService: GuidService,
    private imageService: ImageService,
    private patientService: PatientService,
    private authService: AuthService,
    private adjudicationService: AdjudicationService,
    private loadingSpinnerService: LoadingSpinnerService,
    private searchService: SearchService
  ) {
    this.imageQualityDataSource = new MatTableDataSource();
    this.imageQualityDataSource.data = imageQualityData;
    this.imageQualityColumns = [
      "visitId",
      "eye",
      "color",
      "fluor",
      "faf",
      "octModality",
      "octQuality",
      "blr",
    ];

    this.stagingDataSource = new MatTableDataSource();
    this.stagingDataSource.data = stagingData;
    this.stagingColumns = [
      "visitId",
      "date",
      "gradingDate",
      "eye",
      "graderId",
      "gbStage",
      "mactelStage",
    ];
  }

  async ngOnInit() {
    this.authService.permissions$.subscribe(async (permissions) => {
      this.userPermissions = permissions;
    });
    this.route.params.subscribe((params) => {
      this.dataType = params["dataType"];
      this.dataId = params["dataId"];
      this.buildPage();
      this.tabGroup.selectedIndex = 0;
    });

    this.fileInput = document.getElementById("pedigreeInput");
    this.fileInputForm = document.getElementById("pedigreeInputForm");
    this.resetForm();
  }

  public download() {
    window.location.assign(this.downloadLink);
  }

  async buildPage() {
    this.loadingSpinnerService.start();
    try {
      const dataDetails = await this.dataService.getAllDataDetails(
        this.dataType,
        this.dataId
      );

      this.viewableImages = dataDetails.viewableImages;

      // get web viewable image info
      if (this.dataType === "image") {
        this.viewableImages = await this.imageService.getViewableImages(
          this.dataId
        );
      }

      this.isRecentSearchResults = this.searchService.isRecentSearchResults();
      const data = await this.dataService.getItem(this.dataType, this.dataId);
      if (data === "Image is restricted")
        throw { message: "Image is restricted" };
      data.activityLog = await this.dataService.getActivityLog(
        this.dataType,
        this.dataId
      );
      data.notes = await this.dataService.getNote(this.dataType, this.dataId);
      data.tags = await this.tagService.getTagsForData(
        this.dataType,
        this.dataId
      );

      // Construct detail listings
      this.tabDetails = await this.getTabDetails(
        data[this.dataType + "Id"],
        data.isDicom
      );

      this.data = data;
      if (this.activityLog) {
        this.activityLog.buildLog();
      }
      await this.mactelDiagnosisUpdated();
      this.data.createdDate = moment(this.data.createdDate).format(
        "MMM DD, YYYY"
      );
      if (this.data.familyStatus)
        this.data.familyStatus = Utils.camelToFriendlyCase(data.familyStatus);

      // Get associated tags for patient or image
      let tagsToGroup: Tag[] = data.tags;
      if (tagsToGroup && this.dataType === "patient") {
        tagsToGroup = tagsToGroup.concat(
          await this.tagService.getImageTagsByPatient(this.data.patientId)
        );
      } else if (tagsToGroup && this.dataType === "image") {
        tagsToGroup = tagsToGroup.concat(
          await this.tagService.getPatientTagsByImage(this.data.imageId)
        );
      }
      this.tagGroups = this.groupTags(tagsToGroup);

      // build downloads
      if (["image", "volume", "metabolomic"].includes(this.dataType)) {
        try {
          this.downloadLink = await this.dataService.getSignedUrl(
            this.dataType,
            this.data[this.dataType + "Id"]
          );
          this.fileSize = Utils.formatBytes(this.data.size, 2);
        } catch {}
      }

      // build regular data grids
      if (this.lowyDataTab && this.dataType === "patient") {
        const organizedData = [];
        const dataTabItems: Array<any> =
          await this.dataService.getDataByPatientId(this.data.patientId);
        for (const key in dataTabItems) {
          if (dataTabItems.hasOwnProperty(key)) {
            for (let i = 0; i < dataTabItems[key].length; i++) {
              const datum = dataTabItems[key][i];
              if (key === "image" && datum.metaDatas) {
                for (let j = 0; j < datum.metaDatas.length; j++) {
                  if (datum.metaDatas[j].metaDataMappings) {
                    switch (datum.metaDatas[j].metaDataMappings[0].columnId) {
                      case "STUDY_ID":
                        datum.studyId = datum.metaDatas[j].value;
                        break;
                      case "IMAGE_TYPE":
                        datum.imageType = datum.metaDatas[j].value;
                        break;
                      case "IMAGE_PLATFORM":
                        datum.imagePlatform = datum.metaDatas[j].value;
                        break;
                      case "PHOTO_DATE":
                        datum.photoDate = datum.metaDatas[j].value;
                        break;
                    }
                  }
                }
              }
              datum.datatypeAbbr = key;
              datum.dataType = key;
              if (!datum.isDicom) {
                organizedData.push(datum);
              }
            }
          }
        }

        this.lowyDataTabData = organizedData;
        this.lowyDataTab.dataReady(
          organizedData,
          [
            "checkbox",
            "dataType",
            "name",
            "studyId",
            "visitId",
            "imageType",
            "imagePlatform",
            "photoDate",
          ],
          [
            "checkbox",
            "Data Type",
            "Name",
            "Study ID",
            "Visit ID",
            "Image Type",
            "Image Platform",
            "Photo Date",
          ]
        );
      }

      // build DICOM grids
      if (this.lowyDicomTab && this.dataType === "patient") {
        const organizedData = [];
        const dicomTabItems: Array<any> = await this.dataService.getDicomData(
          this.data.patientId
        );
        for (const key in dicomTabItems) {
          if (dicomTabItems.hasOwnProperty(key)) {
            for (let i = 0; i < dicomTabItems[key].length; i++) {
              const datum = dicomTabItems[key][i];
              if (key === "image" && datum.metaDatas) {
                for (let j = 0; j < datum.metaDatas.length; j++) {
                  if (datum.metaDatas[j].metaDataMappings) {
                    switch (datum.metaDatas[j].metaDataMappings[0].columnId) {
                      case "STUDY_ID":
                        datum.studyId = datum.metaDatas[j].value;
                        break;
                      case "STUDYTYPE":
                        datum.studytype = datum.metaDatas[j].value;
                        break;
                      case "SERIESTYPE":
                        datum.seriestype = datum.metaDatas[j].value;
                        break;
                      case "EYE":
                        datum.eye = datum.metaDatas[j].value;
                        break;
                      case "SERIESCLEAN":
                        datum.seriesclean = datum.metaDatas[j].value;
                        break;
                      // case 'PHOTO_DATE': datum.imageDate = datum.metaDatas[j].value; break;
                      case "STUDYDATE":
                        datum.imageDate = moment(datum.metaDatas[j].value);
                        break;
                    }
                  }
                }
              }
              datum.datatypeAbbr = key;
              datum.dataType = key;
              if (datum.viewableImage) {
                datum.dataType = "viewableImage";
              }
              organizedData.push(datum);
            }
          }
        }

        this.lowyDicomTabData = organizedData;
        this.lowyDicomTab.dataReady(
          organizedData,
          [
            "checkbox",
            "dataType",
            "name",
            "studyId",
            "visitId",
            "seriestype",
            "seriesclean",
            "eye",
            "imageDate",
          ],
          [
            "checkbox",
            "Data Type",
            "Name",
            "Study",
            "Visit",
            "Series Type",
            "Modality",
            "Eye",
            "Image Date",
          ]
        );
      }

      // check for family
      if (this.dataType === "patient") {
        this.family = await this.dataService.getFamilyMembers(
          this.data.familyId
        );
        this.family = _.map(this.family, (familyMember) => {
          _.each(["images", "genetics", "metabolomics"], (dataType) => {
            const count = familyMember[dataType].length;
            if (count > 0) {
              familyMember[dataType] = familyMember[dataType].length;
            }
          });
          return familyMember;
        });
        this.lowyFamilyTabData = this.family;
        this.lowyFamilyTab.dataReady(
          this.family,
          ["checkbox", "patientId", "images", "genetics", "metabolomics"],
          ["checkbox", "Patient ID", "Images", "Genetics", "Metabolomics"]
        );
        this.setupPedigreePhoto();
      }

      // build metaData tab
      if (this.dataType === "patient" && this.userPermissions.viewPatientData) {
        const metaData = await this.patientService.getPatientMetaData(
          this.data.patientId
        );
        this.lowyMetaDataTabData = metaData;
        this.lowyMetaDataTab.dataReady(
          metaData,
          [
            "checkbox",
            "metaDataMappings.dataGroupName",
            "metaDataMappings.columnName",
            "value",
            "studyId",
            "visit",
            "eye",
            "createdAt",
          ],
          [
            "checkbox",
            "Data Group",
            "Column",
            "Value",
            "Study ID",
            "Visit",
            "Eye",
            "Created Date",
          ]
        );
      }
      this.loadingSpinnerService.stop();
    } catch (err) {
      this.loadingSpinnerService.stop();
      this.toastService.throwToast(err.message);
      this.router.navigate(["/home"]);
    }
  }

  private async getTabDetails(dataItem, isDicom) {
    const tabDetails: any = {};
    switch (this.dataType) {
      case "patient": {
        tabDetails.site = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "ENRNHOS",
          "SITE"
        );
        tabDetails.blood = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "ENRNHORPROB",
          "BLD3A"
        );
        tabDetails.nhocont = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "ENRNHORPROB",
          "NHOCONT"
        );
        tabDetails.controlSubject = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "ENRUTAHFAM",
          "CNTRLSUB"
        );
        tabDetails.familyMember = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "ENRUTAHFAM",
          "FAMLYSUB"
        );
        tabDetails.probandRelationship =
          await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "ENRNHORFAMNEW",
            "RLTSHP3E"
          );
        tabDetails.familyStatus = dataItem.familyStatus;
        break;
      }
      case "image": {
        if (!isDicom) {
          tabDetails.imageType = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "IMAGE_TYPE"
          );
          tabDetails.studyId = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "STUDY_ID"
          );
          tabDetails.imagePlatform = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "IMAGE_PLATFORM"
          );
          tabDetails.photoDate = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "PHOTO_DATE"
          );
          tabDetails.eye = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "EYE"
          );
          tabDetails.site = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "SITE"
          );
          tabDetails.comments = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "COMMENTS"
          );
          tabDetails.uploadComments = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "UPLOAD_COMMENTS"
          );
        } else {
          tabDetails.eye = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "EYE"
          );
          tabDetails.s3originpath = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "S3ORIGINPATH"
          );
          tabDetails.studydate = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "STUDYDATE"
          );
          tabDetails.studytype = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "STUDYTYPE"
          );
          tabDetails.seriestype = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "SERIESTYPE"
          );
          tabDetails.modality = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "MODALITY"
          );
          tabDetails.seriesclean = await this.dataService.getSingleMetadata(
            this.dataType,
            dataItem,
            "IMAGES",
            "SERIESCLEAN"
          );
        }
        break;
      }
      case "genetic": {
        tabDetails.fileType = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "GENETICS",
          "FILETYPE"
        );
        tabDetails.path = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "GENETICS",
          "PATH"
        );
        tabDetails.batch = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "GENETICS",
          "BATCH"
        );
        tabDetails.altId = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "GENETICS",
          "ALT_ID"
        );
        tabDetails.specId = await this.dataService.getSingleMetadata(
          this.dataType,
          dataItem,
          "GENETICS",
          "SPEC_ID"
        );
        break;
      }
      default:
        break;
    }
    return tabDetails;
  }

  groupTags(tags: Array<Tag>): { [key: string]: Tag } {
    const tagGroups = {};
    if (!tags) {
      return tagGroups;
    }

    for (let i = 0; i < tags.length; i++) {
      if (!tags[i]) {
        continue;
      }
      const dataType = tags[i].dataTypeId;
      if (!tagGroups[dataType]) {
        tagGroups[dataType] = [];
      }
      tagGroups[dataType].push(tags[i]);
      tagGroups[dataType].open = true;
    }

    return tagGroups;
  }

  public uploadPedigreePicture(file: File) {
    const formData = new FormData();
    let guid = this.guidService.getGuid();
    guid = "pedigree/" + guid;
    formData.append("file", file, guid);
    this.resetForm();

    const emitter = this.uploaderService
      .upload(formData, Bucket.pedigreePictures)
      .subscribe(async (e) => {
        if (!e) {
          emitter.unsubscribe();
          await this.uploaderService.linkPedigree(this.data.familyId, guid);
          this.setupPedigreePhoto();
        } else if (typeof e !== "number") {
          // error, display and remove
          emitter.unsubscribe();
          this.toastService.throwToast("Error uploading file: " + e);
        }
      });
  }

  async setupPedigreePhoto() {
    const family = await this.dataService.getFamily(this.data.familyId);
    if (family && family.pedigreePhotoGuid) {
      this.pedigreePhotoURI = await this.dataService.getSignedUrl(
        "pedigree",
        family.pedigreePhotoGuid
      );
    }
  }

  private resetForm() {
    this.fileInput.value = null;
    this.fileInputForm.reset();
  }

  public gotoPatient(patient: Patient) {
    if (patient.patientId) {
      this.router.navigate(["/data", "patient", patient.patientId]);
    }
  }

  public gotoImage(image: Image) {
    if (image.imageId) {
      this.router.navigate(["/data", "image", image.imageId]);
    }
  }

  public deleteImage() {
    if (this.dataType !== "image" || !this.userPermissions.adminDeleteImage) {
      this.toastService.throwToast("Invalid operation");
      return;
    }
    this.deleteImageModal.open({
      headline: "Delete " + this.data.name,
      subheading:
        "Are you sure you want to delete the image " + this.data.name + "?",
    });
  }

  public async confirmDeleteImage() {
    try {
      await this.dataService.deleteImage(this.data.imageId);
      this.toastService.throwToast("Image deleted");
      this.router.navigate(["/", "data", "patient", this.data.patientId]);
    } catch (err) {
      this.toastService.throwToast(err.message);
    }
  }

  public deletePatient() {
    if (
      this.dataType !== "patient" ||
      !this.userPermissions.adminDeletePatient
    ) {
      this.toastService.throwToast("Invalid operation");
      return;
    }
    this.deletePatientModal.open({
      headline: "Delete " + this.data.patientId,
      subheading:
        "Are you sure you want to delete the patient " +
        this.data.patientId +
        "?",
    });
  }

  public async confirmDeletePatient() {
    try {
      this.loadingSpinnerService.start();
      await this.dataService.deletePatient(this.data.patientId);
      this.loadingSpinnerService.stop();
      this.toastService.throwToast("Patient deleted");
      this.router.navigate(["/home"]);
    } catch (err) {
      this.loadingSpinnerService.stop();
      this.toastService.throwToast(err.message);
    }
  }

  public async tabChanged(tabChangeEvent: MatTabChangeEvent) {
    this.activeTab = tabChangeEvent.tab.textLabel;

    await this.mactelDiagnosisUpdated();
    if (this.activeTab !== "MACTEL DIAGNOSIS") {
      this.data.activityLog = await this.dataService.getActivityLog(
        this.dataType,
        this.dataId
      );
      await Utils.sleep();
      this.activityLog.buildLog();
    } else {
      await Utils.sleep();
    }
  }

  async mactelDiagnosisUpdated() {
    // only patients have mactel diagnosis
    if (this.dataType !== "patient") {
      return;
    }
    await Utils.sleep();
    const adjudication = await this.adjudicationService.getAdjudication(
      this.dataId
    );
    if (adjudication) {
      this.adjudicationActivityLogData = {};
      this.adjudicationActivityLogData.activityLog =
        await this.dataService.getActivityLog(
          "adjudication",
          adjudication.adjudicationId + ""
        );
      this.adjudicationActivityLogData.notes = await this.dataService.getNote(
        "adjudication",
        adjudication.adjudicationId + ""
      );
      if (this.adjudicationActivityLog) {
        this.adjudicationActivityLog.buildLog();
      }
    } else {
      this.adjudicationActivityLogData = null;
    }
    this.data = await this.dataService.getItem(this.dataType, this.dataId);
  }

  async openWebViewable() {
    this.viewableImageModal.open(this.viewableImages);
  }

  public async downloadAllRecords(downloadType: string, tabName: string) {
    switch (tabName) {
      case "DATA":
        this.lowyDataTab.downloadFiles(downloadType, this.lowyDataTabData);
        break;
      case "DICOM":
        this.lowyDicomTab.downloadFiles(downloadType, this.lowyDicomTabData);
        break;
      case "FAMILY":
        this.lowyFamilyTab.downloadFiles(downloadType, this.lowyFamilyTabData);
        break;
      case "META DATA":
        this.lowyMetaDataTab.downloadFiles(
          downloadType,
          this.lowyMetaDataTabData
        );
        break;
    }
  }
}

const imageQualityData = [
  {
    visitId: "000",
    eye: "1",
    color: "4",
    fluor: "4",
    faf: "2",
    octModality: "Stratus",
    octQuality: "1",
    blr: "N/A",
  },
  {
    visitId: "000",
    eye: "2",
    color: "3",
    fluor: "4",
    faf: "2",
    octModality: "Stratus",
    octQuality: "1",
    blr: "N/A",
  },
  {
    visitId: "012",
    eye: "1",
    color: "3",
    fluor: "3",
    faf: "9",
    octModality: "Cirrus",
    octQuality: "2",
    blr: "BLR",
  },
  {
    visitId: "012",
    eye: "2",
    color: "9",
    fluor: "3",
    faf: "9",
    octModality: "Cirrus",
    octQuality: "1",
    blr: "BLR",
  },
  {
    visitId: "024",
    eye: "1",
    color: "3",
    fluor: "3",
    faf: "9",
    octModality: "Cirrus",
    octQuality: "2",
    blr: "N/A",
  },
  {
    visitId: "024",
    eye: "2",
    color: "3",
    fluor: "3",
    faf: "9",
    octModality: "Heidleberg",
    octQuality: "1",
    blr: "BLR",
  },
];

const stagingData = [
  {
    visitId: "000",
    date: "0",
    gradingDate: "39371",
    eye: "1",
    graderId: "1",
    gbStage: "1",
    mactelStage: "1",
  },
  {
    visitId: "000",
    date: "0",
    gradingDate: "39371",
    eye: "2",
    graderId: "1",
    gbStage: "1",
    mactelStage: "2",
  },
  {
    visitId: "012",
    date: "013",
    gradingDate: "39428",
    eye: "1",
    graderId: "1",
    gbStage: "2",
    mactelStage: "1",
  },
  {
    visitId: "012",
    date: "013",
    gradingDate: "39428",
    eye: "2",
    graderId: "1",
    gbStage: "1",
    mactelStage: "1",
  },
  {
    visitId: "024",
    date: "022",
    gradingDate: "39737",
    eye: "1",
    graderId: "3",
    gbStage: "2",
    mactelStage: "1",
  },
  {
    visitId: "024",
    date: "022",
    gradingDate: "39737",
    eye: "2",
    graderId: "3",
    gbStage: "2",
    mactelStage: "2",
  },
];



// WEBPACK FOOTER //
// ./src/app/home/data/data-details.component.ts